<template>
  <div
    class="relative isolate overflow-hidden"
    :class="[invert ? 'text-slate-100' : 'text-slate-900', 'py-6 md:py-8']"
  >
    <div class="relative mx-auto grid place-items-center">
      <component
        :is="headingLevel"
        :id="headingId"
        class="group relative inline-block text-center uppercase font-extrabold leading-[1.05] [text-wrap:balance] outline-none"
        :class="[headingSizeClass]"
      >
        <!-- Backplate for contrast -->
        <span
          aria-hidden="true"
          class="absolute -inset-x-4 -inset-y-3 md:-inset-x-5 md:-inset-y-4 -z-10 rounded-2xl border backdrop-blur-[2px] shadow-[0_18px_70px_-48px_rgba(0,0,0,0.75)]"
          :class="plateClass"
        />

        <!-- Inner highlight -->
        <span
          aria-hidden="true"
          class="pointer-events-none absolute -inset-x-4 -inset-y-3 md:-inset-x-5 md:-inset-y-4 -z-10 rounded-2xl opacity-90"
          :class="ringClass"
        />

        <!-- Text (gradient only on inner span, not on flex) -->
        <span class="relative px-1 inline-block">
          <span
            class="gradient-clip bg-gradient-to-b"
            :class="gradientStopsClass"
          >
            {{ text }}
          </span>

          <!-- Accent line -->
          <span
            aria-hidden="true"
            class="pointer-events-none absolute -bottom-3 left-1/2 h-px w-14 -translate-x-1/2 opacity-75 transition-all duration-300 group-hover:w-24 motion-reduce:transition-none"
            :class="accentLineClass"
          />
        </span>

        <!-- Sheen sweep (hover) -->
        <span
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100 motion-reduce:hidden"
        >
          <span
            class="absolute -inset-y-10 -left-1/2 w-1/3 rotate-12 blur-md translate-x-0 opacity-0 group-hover:left-[120%] group-hover:opacity-100 transition-all duration-700"
            :class="sheenClass"
          />
        </span>
      </component>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    text: string;
    invert?: boolean;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  }>(),
  {
    invert: false,
    headingLevel: 'h1'
  }
);

const headingId = computed(() => {
  const slug = (props.text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9äöüß\s-]/gi, '')
    .replace(/\s+/g, '-')
    .slice(0, 48);

  return slug ? `h-${slug}` : undefined;
});

const headingSizeClass = computed(() => {
  const map: Record<string, string> = {
    h1: 'text-[clamp(1.95rem,3.4vw,3.75rem)]',
    h2: 'text-[clamp(1.75rem,3.0vw,3.25rem)]',
    h3: 'text-[clamp(1.55rem,2.6vw,2.85rem)]',
    h4: 'text-[clamp(1.35rem,2.2vw,2.45rem)]',
    h5: 'text-[clamp(1.20rem,1.9vw,2.10rem)]',
    h6: 'text-[clamp(1.10rem,1.7vw,1.85rem)]'
  };
  return map[props.headingLevel] || map.h1;
});

/**
 * IMPORTANT:
 * - KEIN `text-transparent` mehr hier.
 * - Nur die Gradient-Stops.
 * - Fallback-Farbe kommt vom Wrapper (`text-slate-...`) → Text bleibt auf iOS sichtbar.
 */
const gradientStopsClass = computed(() => {
  const lvl = props.headingLevel;

  const intensity: Record<string, string> = {
    h1: 'from-orange-300 via-orange-100 to-white/95',
    h2: 'from-orange-300 via-orange-100 to-white/90',
    h3: 'from-orange-200 via-orange-100 to-white/85',
    h4: 'from-orange-200 via-orange-50 to-white/80',
    h5: 'from-orange-200 via-orange-50 to-white/75',
    h6: 'from-orange-200 via-orange-50 to-white/70'
  };

  const subtle: Record<string, string> = {
    h1: 'from-slate-950 via-slate-800 to-slate-900',
    h2: 'from-slate-950 via-slate-800 to-slate-900',
    h3: 'from-slate-900 via-slate-700 to-slate-800',
    h4: 'from-slate-900 via-slate-700 to-slate-800',
    h5: 'from-slate-900 via-slate-700 to-slate-800',
    h6: 'from-slate-800 via-slate-700 to-slate-800'
  };

  return props.invert
    ? intensity[lvl] || intensity.h1
    : subtle[lvl] || subtle.h1;
});

const plateClass = computed(() => {
  return props.invert
    ? 'bg-slate-950/40 border-white/10'
    : 'bg-white/75 border-slate-900/10';
});

const ringClass = computed(() => {
  return props.invert
    ? 'bg-[radial-gradient(120%_80%_at_50%_0%,rgba(255,255,255,0.12),transparent_60%)]'
    : 'bg-[radial-gradient(120%_80%_at_50%_0%,rgba(0,0,0,0.06),transparent_60%)]';
});

const accentLineClass = computed(() => {
  return props.invert ? 'bg-orange-300/80' : 'bg-orange-600/70';
});

const sheenClass = computed(() => {
  return props.invert
    ? 'bg-[linear-gradient(to_right,transparent,rgba(255,255,255,0.20),transparent)]'
    : 'bg-[linear-gradient(to_right,transparent,rgba(0,0,0,0.08),transparent)]';
});
</script>

<style scoped>
/* Enable gradient text only when supported (prevents invisible text on buggy/mobile engines) */
@supports ((-webkit-background-clip: text) or (background-clip: text)) {
  .gradient-clip {
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
}

/* prefers-contrast: more — maximize readability */
@media (prefers-contrast: more) {
  .gradient-clip {
    -webkit-text-fill-color: currentColor !important;
    color: currentColor !important;
    background: none !important;
  }

  .backdrop-blur-\[2px\] {
    backdrop-filter: none !important;
  }
}
</style>
